'use client'

import { ColumnDef } from '@tanstack/react-table'
import { Button } from '../../../components/ui/button'
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react'

export type User = {
    id: string
    firstName: string
    lastName: string
    email: string
    role: string
    isActive: boolean
    emailVerified: boolean
    business: { id: string; name: string } | null
}

const SortableHeader = ({
    column,
    label,
}: {
    column: any
    label: string
}) => (
    <Button
        variant="ghost"
        size="sm"
        className="-ml-3 h-8 text-xs font-medium text-muted-foreground uppercase tracking-wider"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
    >
        {label}
        {column.getIsSorted() === 'asc' ? (
            <ChevronUp className="ml-1.5 h-3.5 w-3.5" />
        ) : column.getIsSorted() === 'desc' ? (
            <ChevronDown className="ml-1.5 h-3.5 w-3.5" />
        ) : (
            <ChevronsUpDown className="ml-1.5 h-3.5 w-3.5" />
        )}
    </Button>
)

export const columns: ColumnDef<User>[] = [
    {
        accessorFn: (row) => `${row.firstName} ${row.lastName}`,
        id: 'name',
        header: ({ column }) => (
            <SortableHeader column={column} label="Name" />
        ),
        cell: ({ row }) => (
            <span className="text-sm font-medium text-foreground">
                {row.original.firstName} {row.original.lastName}
            </span>
        ),
    },
    {
        accessorKey: 'email',
        header: ({ column }) => (
            <SortableHeader column={column} label="Email" />
        ),
        cell: ({ row }) => (
            <span className="text-sm text-muted-foreground font-mono">
                {row.original.email}
            </span>
        ),
    },
    {
        accessorKey: 'business',
        header: 'Business',
        cell: ({ row }) =>
            row.original.business?.name ? (
                <span className="text-sm text-foreground">
                    {row.original.business.name}
                </span>
            ) : (
                <span className="text-sm text-muted-foreground">—</span>
            ),
    },
    {
        accessorKey: 'role',
        header: 'Role',
        cell: ({ row }) => (
            <span className="text-sm text-foreground capitalize">
                {row.original.role?.replace(/([A-Z])/g, ' $1').trim()}
            </span>
        ),
    },
    {
        accessorKey: 'emailVerified',
        header: 'Verified',
        cell: ({ row }) =>
            row.original.emailVerified ? (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
                    Verified
                </span>
            ) : (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                    Pending
                </span>
            ),
    },
    {
        accessorKey: 'isActive',
        header: 'Status',
        cell: ({ row }) =>
            row.original.isActive ? (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
                    Active
                </span>
            ) : (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
                    Inactive
                </span>
            ),
    },
]
